import { useEffect, useRef, useState } from 'react';
import { Send, MessageSquare, AlertCircle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { chatApi, ApiError } from '@solara/api-client';
import type { ChatMessageDto, ChatThreadDto } from '@solara/api-client';
import { useSession } from '../auth/SessionContext';

export default function ChatPage() {
  const { t } = useTranslation('p1');
  const { user } = useSession();
  const [threads, setThreads] = useState<ChatThreadDto[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void bootstrap();
  }, []);

  useEffect(() => {
    if (activeId) {
      void loadMessages(activeId);
      const poll = setInterval(() => void loadMessages(activeId), 8000);
      return () => clearInterval(poll);
    }
  }, [activeId]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages]);

  async function bootstrap() {
    try {
      let list = await chatApi.listThreads();
      if (list.length === 0) {
        const created = await chatApi.ensureThread();
        list = [created];
      }
      setThreads(list);
      setActiveId(list[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('chat.loadFailed'));
    }
  }

  async function loadMessages(threadId: string) {
    try {
      setMessages(await chatApi.messages(threadId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('chat.loadMessagesFailed'));
    }
  }

  async function send() {
    if (!activeId || !input.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await chatApi.post(activeId, input);
      setInput('');
      await loadMessages(activeId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('chat.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('chat.title')}</h1>
        <p className="text-fg-muted text-sm">{t('chat.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            {threads.find((th) => th.id === activeId)?.title ?? t('chat.defaultThreadTitle')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div ref={scroller} className="h-[28rem] overflow-y-auto space-y-3 border border-border rounded p-3 bg-surface">
            {messages.length === 0 ? (
              <p className="py-12 text-center text-sm text-fg-muted">{t('chat.noMessages')}</p>
            ) : (
              messages.map((m) => {
                const mine = m.senderUserId === user?.id;
                return (
                  <div key={m.id} className={mine ? 'flex justify-end' : 'flex justify-start'}>
                    <div
                      className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                        mine ? 'bg-brand-600 text-white' : 'bg-surface-muted text-fg'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <p className={`mt-1 text-[10px] ${mine ? 'text-white/80' : 'text-fg-muted'}`}>
                        {new Date(m.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
            className="mt-3 flex items-center gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chat.typeMessage')}
              className="flex-1"
            />
            <Button type="submit" disabled={busy || !input.trim()}>
              <Send className="mr-1 h-3 w-3" />
              {t('chat.send')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
